import { useCallback, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/**
 * Filtros globais do modulo Financeiro, persistidos na URL (querystring).
 *
 * - Periodo (date_start/date_end) com DEFAULT = mes atual; o usuario pode
 *   ampliar/escolher outro periodo. Estado na URL => link compartilhavel + F5.
 * - Franquia(s) (conta_origem): multi-base. Vazio = consolidado (todas).
 * - Busca (q): pessoa_nome / descricao.
 *
 * A FinanceiroTopbar global (montada no layout) escreve os filtros na URL;
 * cada tela le `apiParams` daqui para passar aos hooks de dados.
 */

const pad = (n: number) => String(n).padStart(2, '0');
const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const monthEnd = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
// Parse 'YYYY-MM-DD' como data LOCAL (evita o shift de fuso do new Date(string)).
const parseISO = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/** Telas de série temporal usam o ano corrente como default; as demais, o mês. */
const YEAR_DEFAULT_SEGS = ['diretoria', 'fluxo', 'gaps'];
const routeDefaultPeriod = (pathname: string): 'month' | 'year' => {
  const seg = pathname.split('/modulesHome/financeiro/')[1]?.split('/')[0] ?? '';
  return YEAR_DEFAULT_SEGS.includes(seg) ? 'year' : 'month';
};

export interface FinanceiroFiltersApi {
  date_start: string;
  date_end: string;
  conta_origem?: string;
  /** Busca (pessoa_nome/descrição). Vai junto p/ a tela filtrar pela entidade. */
  q?: string;
}

export interface FinanceiroFilters {
  dateStart: string;
  dateEnd: string;
  contaOrigem: string[];
  q: string;
  isFullMonth: boolean;
  monthLabel: string;
  apiParams: FinanceiroFiltersApi;
  /** Mesmo recorte, deslocado para o período imediatamente anterior (p/ Δ%). */
  prevApiParams: FinanceiroFiltersApi;
  /** Recorte por mês/ano (derivado das datas) para o seletor de período. */
  range: { iniM: number; iniY: number; fimM: number; fimY: number };
  /** Seta o período por mês/ano (com clamp: início ≤ fim). */
  setRangeMonths: (iniM: number, iniY: number, fimM: number, fimY: number) => void;
  /** Atalhos de período (relativos a hoje). */
  atalhos: Record<string, () => void>;
  setPeriodo: (start: string, end: string) => void;
  prevMonth: () => void;
  nextMonth: () => void;
  setMonthOf: (isoDate: string) => void;
  setContaOrigem: (contas: string[]) => void;
  setQ: (q: string) => void;
}

export interface UseFinanceiroFiltersOpts {
  /** Período default quando a URL não traz datas. 'month' = mês atual (padrão),
   *  'year' = ano corrente (telas de série temporal, ex: Fluxo). */
  defaultPeriod?: 'month' | 'year';
}

export const useFinanceiroFilters = (opts?: UseFinanceiroFiltersOpts): FinanceiroFilters => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const hoje = new Date();

  // Default do período: explícito (opts) vence; senão, derivado da rota — assim
  // a topbar global e a tela concordam mesmo sem a tela passar nada.
  const effectiveDefault = opts?.defaultPeriod ?? routeDefaultPeriod(pathname);
  const defStart =
    effectiveDefault === 'year' ? `${hoje.getFullYear()}-01-01` : toISO(monthStart(hoje));
  const defEnd =
    effectiveDefault === 'year' ? `${hoje.getFullYear()}-12-31` : toISO(monthEnd(hoje));

  const dateStart = searchParams.get('date_start') || defStart;
  const dateEnd = searchParams.get('date_end') || defEnd;
  const contaOrigem = (searchParams.get('conta_origem') || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  const q = searchParams.get('q') || '';

  // Materializa o período na URL: carimba o default da ROTA quando ausente OU
  // quando o que veio é um default HERDADO de outra tela (mês↔ano). Nunca por
  // cima de uma escolha explícita do usuário (pset=1). Assim o filtro fica SEMPRE
  // no search (reload/link fiéis, base do fix #533) sem quebrar o default por-rota
  // (hub=mês não força ano na Diretoria, e vice-versa).
  const userSetPeriodo = searchParams.get('pset') === '1';
  useEffect(() => {
    if (userSetPeriodo) return;
    const rawStart = searchParams.get('date_start');
    const rawEnd = searchParams.get('date_end');
    if (rawStart === defStart && rawEnd === defEnd) return; // já é o default da rota
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('date_start', defStart);
        next.set('date_end', defEnd);
        return next; // preserva conta_origem/q/page
      },
      { replace: true },
    );
  }, [userSetPeriodo, defStart, defEnd, searchParams, setSearchParams]);

  const patch = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          });
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPeriodo = (start: string, end: string) =>
    patch({ date_start: start, date_end: end, pset: '1' });

  const setMonthOf = (isoDate: string) => {
    const d = parseISO(isoDate);
    setPeriodo(toISO(monthStart(d)), toISO(monthEnd(d)));
  };

  const shiftMonth = (delta: number) => {
    const d = parseISO(dateStart);
    const alvo = new Date(d.getFullYear(), d.getMonth() + delta, 1);
    setPeriodo(toISO(monthStart(alvo)), toISO(monthEnd(alvo)));
  };

  const setContaOrigem = (contas: string[]) =>
    patch({ conta_origem: contas.length ? contas.join(',') : null });

  const setQ = (novo: string) => patch({ q: novo || null });

  // Rotulo do mes so quando o intervalo cobre exatamente um mes-calendario.
  const ini = parseISO(dateStart);
  const isFullMonth = dateStart === toISO(monthStart(ini)) && dateEnd === toISO(monthEnd(ini));
  const monthLabel = isFullMonth
    ? `${MESES[ini.getMonth()]}/${ini.getFullYear()}`
    : `${dateStart.split('-').reverse().join('/')} – ${dateEnd.split('-').reverse().join('/')}`;

  // Período anterior: mesmo nº de meses-calendário, deslocado para trás. (Duração
  // em ms dava off-by-one — jul→jun começava em 31/05 porque jun tem 30 dias.)
  const fim = parseISO(dateEnd);
  const mesesSpan =
    (fim.getFullYear() - ini.getFullYear()) * 12 + (fim.getMonth() - ini.getMonth()) + 1;
  const prevIni = new Date(ini.getFullYear(), ini.getMonth() - mesesSpan, 1);
  const prevFim = new Date(fim.getFullYear(), fim.getMonth() - mesesSpan + 1, 0);

  // Recorte por mês/ano (para os selects do PeriodPicker) + clamp + atalhos.
  const range = {
    iniM: ini.getMonth(),
    iniY: ini.getFullYear(),
    fimM: parseISO(dateEnd).getMonth(),
    fimY: parseISO(dateEnd).getFullYear(),
  };
  const setRangeMonths = (iniM: number, iniY: number, fimM: number, fimY: number) => {
    const abs = (m: number, y: number) => y * 12 + m;
    let fM = fimM;
    let fY = fimY;
    if (abs(iniM, iniY) > abs(fimM, fimY)) {
      fM = iniM;
      fY = iniY;
    }
    setPeriodo(toISO(monthStart(new Date(iniY, iniM, 1))), toISO(monthEnd(new Date(fY, fM, 1))));
  };
  const ano = hoje.getFullYear();
  const atalhos: Record<string, () => void> = {
    esteAno: () => setPeriodo(`${ano}-01-01`, `${ano}-12-31`),
    anoPassado: () => setPeriodo(`${ano - 1}-01-01`, `${ano - 1}-12-31`),
    primSem: () => setPeriodo(`${ano}-01-01`, `${ano}-06-30`),
    segSem: () => setPeriodo(`${ano}-07-01`, `${ano}-12-31`),
    trimAtual: () => {
      const tri = Math.floor(hoje.getMonth() / 3);
      setPeriodo(
        toISO(monthStart(new Date(ano, tri * 3, 1))),
        toISO(monthEnd(new Date(ano, tri * 3 + 2, 1))),
      );
    },
    ult12m: () => setPeriodo(toISO(new Date(ano, hoje.getMonth() - 11, 1)), toISO(monthEnd(hoje))),
  };

  return {
    dateStart,
    dateEnd,
    contaOrigem,
    q,
    isFullMonth,
    monthLabel,
    apiParams: {
      date_start: dateStart,
      date_end: dateEnd,
      conta_origem: contaOrigem.length ? contaOrigem.join(',') : undefined,
      q: q || undefined,
    },
    prevApiParams: {
      date_start: toISO(prevIni),
      date_end: toISO(prevFim),
      conta_origem: contaOrigem.length ? contaOrigem.join(',') : undefined,
      q: q || undefined,
    },
    range,
    setRangeMonths,
    atalhos,
    setPeriodo,
    prevMonth: () => shiftMonth(-1),
    nextMonth: () => shiftMonth(1),
    setMonthOf,
    setContaOrigem,
    setQ,
  };
};
